/**
 * 인증 API 훅 (Phase F.2 갱신).
 *
 * - 로그인 → access + refresh 페어 저장
 * - 회원가입 → 선택적 PIPA 동의
 * - 본인 조회 (role + consent 정보 포함)
 * - 로그아웃 → DB의 refresh 폐기 + 로컬 정리
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api, getRefreshToken, setTokens } from './client'

export type UserRole = 'admin' | 'inspector' | 'viewer'
export type SignatureRole = 'inspector' | 'site_manager' | 'supervisor'

export interface User {
  id: string
  tenant_id: string
  email: string
  full_name: string | null
  is_active: boolean
  is_superuser: boolean
  role: UserRole
  signature_role: SignatureRole | null
  consent_terms_version: string | null
  consent_privacy_version: string | null
  consent_at: string | null
  created_at?: string | null
}

export interface TokenResponse {
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
  refresh_expires_in: number
}

export interface ConsentInput {
  terms_version: string
  privacy_version: string
  marketing: boolean
}

// 현재 사용 중인 약관/개인정보 버전 (회원가입 모달이 표시 + DB 저장)
export const CURRENT_TERMS_VERSION = 'v1.0'
export const CURRENT_PRIVACY_VERSION = 'v1.0'

// ─────────────────────────────────────────────────────────────────
// Hooks
// ─────────────────────────────────────────────────────────────────

export function useCurrentUser() {
  return useQuery({
    queryKey: ['me'],
    queryFn: async () => (await api.get<User>('/api/v1/auth/me')).data,
    retry: false,
    staleTime: 60_000,
  })
}

export function useLogin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (creds: { email: string; password: string }) => {
      const form = new URLSearchParams()
      form.append('username', creds.email)
      form.append('password', creds.password)
      const res = await api.post<TokenResponse>('/api/v1/auth/login', form)
      return res.data
    },
    onSuccess: ({ access_token, refresh_token }) => {
      setTokens(access_token, refresh_token)
      qc.invalidateQueries({ queryKey: ['me'] })
    },
  })
}

export interface RegisterInput {
  invitation_token: string
  email: string
  password: string
  full_name?: string
  consent?: ConsentInput
}

export function useRegister() {
  return useMutation({
    mutationFn: async (input: RegisterInput) =>
      (await api.post<User>('/api/v1/auth/register', input)).data,
  })
}

/**
 * 백엔드 refresh 폐기 + 로컬 토큰 정리.
 * 백엔드 호출 실패해도 로컬은 무조건 정리 (토큰 만료 등 정상 케이스).
 */
export async function logout(qc?: ReturnType<typeof useQueryClient>) {
  const refresh = getRefreshToken()
  if (refresh) {
    try {
      await api.post('/api/v1/auth/logout', { refresh_token: refresh })
    } catch {
      /* 토큰 만료 등 — 무시 */
    }
  }
  setTokens(null, null)
  qc?.clear()
}

// ─────────────────────────────────────────────────────────────────
// Role helpers
// ─────────────────────────────────────────────────────────────────

export const ROLE_LABEL: Record<UserRole, string> = {
  admin: '관리자',
  inspector: '검측자',
  viewer: '조회 전용',
}

export function canMutate(role: UserRole | undefined): boolean {
  return role === 'admin' || role === 'inspector'
}

export function isAdmin(role: UserRole | undefined): boolean {
  return role === 'admin'
}
